import * as React from "react";
import { Box, Stack, styled, Typography } from "@mui/material";
import GraphicEqOutlinedIcon from "@mui/icons-material/GraphicEqOutlined";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import SettingsIcon from "@mui/icons-material/Settings";

export interface ContentCardProps {
  type: "trailer" | "audio-mix" | "subtitle";
  title: string;
  audioList: string[];
  subtitleList: string[];
  formatList: string[];
  qualityList: string[];
}

const ContentCard: React.FC<ContentCardProps> = (props) => {
  const { title, type, audioList, subtitleList, formatList, qualityList } = props;

  let cover: string;

  switch (type) {
    case "trailer":
      cover = "/static/images/temp/content-trailer.png";
      break;
    case "audio-mix":
      cover = "/static/images/temp/content-audio-mix.png";
      break;
    case "subtitle":
      cover = "/static/images/temp/content-subtitle.png";
      break;
  }

  return (
    <CardWrapper alignItems="flex-start">
      <CardSquare sx={{ mb: 1.5 }}>
        <CardCoverWrapper justifyContent="center" alignItems="center">
          <CardCover src={cover} />
        </CardCoverWrapper>
      </CardSquare>
      <Typography mb={0.5} fontWeight={500}>
        {title}
      </Typography>
      <CardTypeTag type={type} sx={{ mb: 1.5 }}>
        <Typography fontWeight={500} variant="body2" sx={{ textTransform: "capitalize" }}>
          {type}
        </Typography>
      </CardTypeTag>
      {audioList.length > 0 && (
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
          <IconWrapper sx={{ mr: 1 }}>
            <GraphicEqOutlinedIcon />
          </IconWrapper>
          {audioList.map((audio, i) => (
            <Typography color="text.secondary" variant="h6" sx={{ mr: 1 }} key={audio}>
              {audio}
            </Typography>
          ))}
        </Stack>
      )}
      {formatList.length > 0 && (
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
          <IconWrapper sx={{ mr: 1 }}>
            <SettingsIcon />
          </IconWrapper>
          {formatList.map((format) => (
            <Typography color="text.secondary" variant="h6" sx={{ mr: 1 }} key={format}>
              {format}
            </Typography>
          ))}
        </Stack>
      )}
      {subtitleList.length > 0 && (
        <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
          <IconWrapper sx={{ mr: 1 }}>
            <ChatOutlinedIcon />
          </IconWrapper>
          {subtitleList.map((subtitle) => (
            <Typography color="text.secondary" variant="h6" sx={{ mr: 1 }} key={subtitle}>
              {subtitle}
            </Typography>
          ))}
        </Stack>
      )}
      {qualityList.length > 0 && (
        <Stack direction="row" alignItems="center">
          <IconWrapper sx={{ mr: 1 }}>
            <TvOutlinedIcon />
          </IconWrapper>
          {qualityList.map((quality) => (
            <Typography color="text.secondary" variant="h6" sx={{ mr: 1 }} key={quality}>
              {quality}
            </Typography>
          ))}
        </Stack>
      )}
    </CardWrapper>
  );
};

export default ContentCard;

const CardWrapper = styled(Stack)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.backgroundAlpha.main}`,
  padding: theme.spacing(1),
  height: "100%",
}));

const CardSquare = styled(Box)(({ theme }) => ({
  width: "100%",
  borderRadius: theme.spacing(1),
  overflow: "hidden",
  position: "relative",
  "&::after": {
    content: "''",
    display: "block",
    paddingBottom: "100%",
  },
}));

const CardTypeTag = styled(Box, { shouldForwardProp: (propName) => propName !== "type" })<{
  type: ContentCardProps["type"];
}>(({ type, theme }) => ({
  padding: "2px 5px",
  borderRadius: "6px",
  ...(type === "trailer" && {
    background: theme.palette.primary.alpha,
  }),
  ...(type === "audio-mix" && {
    background: theme.palette.info.alpha,
  }),
  ...(type === "subtitle" && {
    background: theme.palette.success.alpha,
  }),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  path: {
    color: theme.palette.text.secondary,
  },
}));

const CardCoverWrapper = styled(Stack)(() => ({
  position: "absolute",
  top: 0,
  left: 0,
  height: "100%",
  width: "100%",
}));

const CardCover = styled("img")(() => ({
  height: "100%",
  width: "100%",
  objectFit: "cover",
}));
